// src/components/billing/TopCustomersTable.jsx
import React from 'react';

const TopCustomersTable = ({ customers, loading }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp);
    }
    
    if (isNaN(date.getTime())) return 'N/A';
    
    return date.toLocaleDateString('en-IN', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCustomerInitials = (name) => {
    if (!name) return 'UK';
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
  };

  const getInitialsColor = (name) => {
    if (!name) return 'bg-gray-500';
    const colors = [
      'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 
      'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!customers || customers.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No customers found</h3>
        <p className="mt-1 text-sm text-gray-500">No customer transactions in the selected period.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Desktop Table View */}
      <div className="hidden lg:block flex-1 overflow-y-auto">
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Revenue
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Invoices
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Invoice
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {customers.map((customer, index) => (
              <tr key={`customer-${index}`} className="hover:bg-gray-50 transition-colors duration-150">
                <td className="px-4 py-3">
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 h-8 w-8 rounded-full ${getInitialsColor(customer.name)} flex items-center justify-center mr-3`}>
                      <span className="text-white text-xs font-medium">
                        {getCustomerInitials(customer.name)}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {customer.name || 'Unknown Customer'}
                      </div>
                      {customer.email && (
                        <div className="text-xs text-gray-500 truncate">
                          {customer.email}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                
                <td className="px-4 py-3 text-right">
                  <div className="text-sm font-semibold text-gray-900">
                    {formatCurrency(customer.totalAmount)}
                  </div>
                  {customer.totalAmount > 0 && (
                    <div className="text-xs text-gray-500">
                      Avg: {formatCurrency(customer.totalAmount / (customer.invoiceCount || 1))}
                    </div>
                  )}
                </td>
                
                <td className="px-4 py-3 text-center">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {customer.invoiceCount || 0}
                  </span>
                </td>
                
                <td className="px-4 py-3 text-center">
                  <div className="text-sm text-gray-900">
                    {formatDate(customer.lastInvoiceDate)}
                  </div>
                  {customer.lastInvoiceDate && (
                    <div className="text-xs text-gray-500">
                      {(() => {
                        const lastDate = customer.lastInvoiceDate.toDate ? 
                          customer.lastInvoiceDate.toDate() : 
                          new Date(customer.lastInvoiceDate);
                        const daysDiff = Math.floor((new Date() - lastDate) / (1000 * 60 * 60 * 24));
                        
                        if (daysDiff === 0) return 'Today';
                        if (daysDiff === 1) return 'Yesterday';
                        if (daysDiff < 7) return `${daysDiff} days ago`;
                        if (daysDiff < 30) return `${Math.floor(daysDiff / 7)} weeks ago`;
                        if (daysDiff < 365) return `${Math.floor(daysDiff / 30)} months ago`;
                        return `${Math.floor(daysDiff / 365)} years ago`;
                      })()}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile/Tablet Card View */}
      <div className="lg:hidden flex-1 overflow-y-auto space-y-3">
        {customers.map((customer, index) => (
          <div key={`customer-mobile-${index}`} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-150">
            <div className="flex items-start justify-between">
              <div className="flex items-center flex-1 min-w-0">
                <div className={`flex-shrink-0 h-10 w-10 rounded-full ${getInitialsColor(customer.name)} flex items-center justify-center mr-3`}>
                  <span className="text-white text-sm font-medium">
                    {getCustomerInitials(customer.name)}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {customer.name || 'Unknown Customer'}
                  </div>
                  {customer.email && (
                    <div className="text-xs text-gray-500 truncate">
                      {customer.email}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right ml-4">
                <div className="text-sm font-semibold text-gray-900">
                  {formatCurrency(customer.totalAmount)}
                </div>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {customer.invoiceCount || 0} invoices
                </span>
              </div>
            </div>
            
            <div className="mt-3 flex justify-between items-center text-xs text-gray-500">
              <div>
                Avg: {formatCurrency(customer.totalAmount / (customer.invoiceCount || 1))}
              </div>
              <div>
                Last: {formatDate(customer.lastInvoiceDate)}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Summary Footer */}
      {customers.length > 0 && (
        <div className="mt-auto pt-3 border-t border-gray-200 bg-gray-50 px-4 py-2 rounded-b-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span className="text-xs text-gray-600">
              Top {customers.length} customers by revenue
            </span>
            <span className="text-xs font-medium text-gray-900">
              Total: {formatCurrency(customers.reduce((sum, customer) => sum + (customer.totalAmount || 0), 0))}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopCustomersTable;